import React, { useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Card, Col, Container, Dropdown, FloatingLabel, Form, Modal, Row, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "./Navbar";
import PageTitle from "./components/PageTitle";
import { API_ENDPOINTS } from "./config/api";
import RichTextEditor from "./components/RichTextEditor";
import UserAvatar from "./components/UserAvatar";

const DiscussionPostPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const userRole = localStorage.getItem("role") || "";
  const currentUserId = Number(localStorage.getItem("userId") || 0);
  const token = useMemo(() => localStorage.getItem("token"), []);
  const hasRecordedViewRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [postingComment, setPostingComment] = useState(false);

  const [replyToId, setReplyToId] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState({ title: "", categoryId: "", hashtags: "", contentHtml: "" });
  const [myReaction, setMyReaction] = useState(null); // 'like' | 'dislike' | null
  const [likeCount, setLikeCount] = useState(0);
  const [dislikeCount, setDislikeCount] = useState(0);

  const fetchPost = async (options = { incView: true }) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_ENDPOINTS.DISCUSSIONS}/posts/${id}?incView=${options.incView ? "1" : "0"}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed");
      setPost(data);
      setMyReaction(data.myReaction || null);
      setLikeCount(data.likeCount || 0);
      setDislikeCount(data.dislikeCount || 0);
    } catch (e) {
      toast.error(e.message || "Failed to load post");
      setPost(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    setCommentsLoading(true);
    try {
      const res = await fetch(`${API_ENDPOINTS.DISCUSSIONS}/posts/${id}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed");
      setComments(Array.isArray(data) ? data : []);
    } catch (e) {
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const res = await fetch(`${API_ENDPOINTS.DISCUSSIONS}/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => []);
      setCategories(Array.isArray(data) ? data : []);
    } catch {
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  useEffect(() => {
    // In React 18 StrictMode, effects may run twice in development.
    // Guard so we only send one incView=1 per mount for this id.
    const shouldIncView = !hasRecordedViewRef.current;
    if (shouldIncView) {
      hasRecordedViewRef.current = true;
    }
    fetchPost({ incView: shouldIncView });
    fetchComments();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggleLike = async (commentId) => {
    const prev = comments;
    setComments((list) =>
      list.map((c) =>
        c.id !== commentId
          ? c
          : {
              ...c,
              likedByMe: !c.likedByMe,
              likeCount: Math.max(0, (c.likeCount || 0) + (c.likedByMe ? -1 : 1))
            }
      )
    );

    try {
      const res = await fetch(`${API_ENDPOINTS.DISCUSSIONS}/comments/${commentId}/like`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed");
      setComments((list) =>
        list.map((c) => (c.id === commentId ? { ...c, likeCount: data.likeCount, likedByMe: data.likedByMe } : c))
      );
    } catch (e) {
      setComments(prev);
      toast.error("Failed to update like");
    }
  };

  const submitComment = async () => {
    let text = commentText;
    if (replyToId) {
      if (typeof document !== "undefined") {
        const el = document.getElementById(`reply-text-${replyToId}`);
        text = el ? el.value : "";
      }
    }
    if (!text || !text.trim()) return toast.error("Comment cannot be empty");
    setPostingComment(true);
    try {
      const escapeHtml = (s) =>
        String(s)
          .replaceAll("&", "&amp;")
          .replaceAll("<", "&lt;")
          .replaceAll(">", "&gt;")
          .replaceAll('"', "&quot;")
          .replaceAll("'", "&#039;");

      const contentHtml = `<p>${escapeHtml(text).replaceAll("\n", "<br/>")}</p>`;

      const res = await fetch(`${API_ENDPOINTS.DISCUSSIONS}/posts/${id}/comments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contentHtml, parentCommentId: replyToId || null })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed");
      setComments((list) => [...list, data]);
      if (!replyToId) {
        setCommentText("");
      }
      setReplyToId(null);
      toast.success("Comment posted");
    } catch (e) {
      toast.error(e.message || "Failed to post comment");
    } finally {
      setPostingComment(false);
    }
  };

  const togglePostPin = async () => {
    if (userRole !== "director" || !post?.id) return;
    const prev = post;
    setPost((p) => ({ ...p, pinned: !p.pinned }));
    try {
      const res = await fetch(`${API_ENDPOINTS.DISCUSSIONS}/posts/${post.id}/pin`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed");
      setPost((p) => ({ ...p, pinned: data.pinned }));
      toast.success(data.pinned ? "Pinned" : "Unpinned");
    } catch (e) {
      setPost(prev);
      toast.error("Failed to toggle pin");
    }
  };

  const reactToPost = async (type) => {
    const nextType = myReaction === type ? null : type;
    // optimistic
    setMyReaction(nextType);
    if (type === 'like') {
      setLikeCount((c) => c + (nextType === 'like' ? 1 : myReaction === 'like' ? -1 : 0));
      if (myReaction === 'dislike' && nextType !== 'dislike') {
        setDislikeCount((c) => Math.max(0, c - 1));
      }
    } else if (type === 'dislike') {
      setDislikeCount((c) => c + (nextType === 'dislike' ? 1 : myReaction === 'dislike' ? -1 : 0));
      if (myReaction === 'like' && nextType !== 'like') {
        setLikeCount((c) => Math.max(0, c - 1));
      }
    }

    try {
      const res = await fetch(`${API_ENDPOINTS.DISCUSSIONS}/posts/${post.id}/react`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ type: nextType })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed");
      setMyReaction(data.myReaction || null);
      setLikeCount(data.likeCount || 0);
      setDislikeCount(data.dislikeCount || 0);
    } catch (e) {
      // Reload counts from server without incrementing views again
      await fetchPost({ incView: false });
    }
  };

  const canManagePost = useMemo(() => {
    if (!post) return false;
    if (userRole === "director") return true;
    return Number(post.createdBy) === Number(currentUserId);
  }, [post, userRole, currentUserId]);

  const openEdit = () => {
    if (!post) return;
    setEditForm({
      title: post.title || "",
      categoryId: post.categoryId ? String(post.categoryId) : "",
      hashtags: Array.isArray(post.hashtags) ? post.hashtags.map((t) => `#${t}`).join(" ") : "",
      contentHtml: post.contentHtml || ""
    });
    setShowEditModal(true);
  };

  const saveEdit = async () => {
    if (!editForm.title.trim()) return toast.error("Title is required");
    if (!editForm.contentHtml || !editForm.contentHtml.trim()) return toast.error("Content is required");
    setSavingEdit(true);
    try {
      const res = await fetch(`${API_ENDPOINTS.DISCUSSIONS}/posts/${post.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: editForm.title.trim(),
          categoryId: editForm.categoryId ? Number(editForm.categoryId) : null,
          hashtags: editForm.hashtags,
          contentHtml: editForm.contentHtml
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed");
      setPost(data);
      setMyReaction(data.myReaction || null);
      setLikeCount(data.likeCount || 0);
      setDislikeCount(data.dislikeCount || 0);
      setShowEditModal(false);
      toast.success("Post updated");
    } catch (e) {
      toast.error(e.message || "Failed to update post");
    } finally {
      setSavingEdit(false);
    }
  };

  const deletePost = async () => {
    if (!post) return;
    const ok = window.confirm("Delete this post? This cannot be undone.");
    if (!ok) return;
    try {
      const res = await fetch(`${API_ENDPOINTS.DISCUSSIONS}/posts/${post.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Failed");
      toast.success("Post deleted");
      navigate("/discussions");
    } catch (e) {
      toast.error(e.message || "Failed to delete post");
    }
  };

  const commentTree = useMemo(() => {
    const byId = new Map();
    const roots = [];
    (comments || []).forEach((c) => byId.set(Number(c.id), { ...c, replies: [] }));
    byId.forEach((c) => {
      const parentId = c.parentCommentId ? Number(c.parentCommentId) : null;
      if (parentId && byId.has(parentId)) byId.get(parentId).replies.push(c);
      else roots.push(c);
    });
    const sortFn = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);
    const sortDeep = (node) => {
      node.replies.sort(sortFn);
      node.replies.forEach(sortDeep);
    };
    roots.sort(sortFn);
    roots.forEach(sortDeep);
    return roots;
  }, [comments]);

  const CommentNode = ({ node, depth }) => {
    const indent = Math.min(depth, 6) * 18;
    return (
      <div style={{ marginLeft: indent }}>
        <div className="d-flex gap-3">
          <div>
            <UserAvatar user={node.creator} size={40} alt="" />
          </div>
          <div className="flex-grow-1">
            <div className="d-flex align-items-center flex-wrap gap-2">
              <span className="fw-semibold">
                {node.creator?.firstName} {node.creator?.lastName}
              </span>
              <span className="text-muted small">
                • {node.creator?.role ? node.creator.role : "User"} • {new Date(node.createdAt).toLocaleString()}
              </span>
            </div>
            <div
              className="eval-rich-content mt-1"
              style={{ lineHeight: 1.7 }}
              dangerouslySetInnerHTML={{ __html: node.contentHtml }}
            />
            <div className="d-flex align-items-center gap-3 mt-2 small text-muted">
              <Button
                variant="link"
                className="p-0 text-decoration-none d-flex align-items-center gap-1"
                onClick={() => toggleLike(node.id)}
              >
                <i className={`bi ${node.likedByMe ? "bi-hand-thumbs-up-fill" : "bi-hand-thumbs-up"}`} />
                {node.likeCount || 0}
              </Button>
              <Button
                variant="link"
                className="p-0 text-decoration-none"
                onClick={() => setReplyToId(node.id)}
              >
                Reply
              </Button>
            </div>

            {replyToId === node.id && (
              <div className="mt-3">
                <div className="d-flex gap-2 align-items-start">
                  <Form.Control
                    as="textarea"
                    rows={1}
                    id={`reply-text-${node.id}`}
                    placeholder="Write a reply…"
                    autoFocus
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    className="flex-shrink-0"
                    disabled={postingComment}
                    onClick={submitComment}
                  >
                    {postingComment ? "Posting…" : "Reply"}
                  </Button>
                  <Button
                    variant="light-solid"
                    className="flex-shrink-0"
                    size="sm"
                    onClick={() => setReplyToId(null)}
                  >
                    <i className="bi bi-x-lg" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {Array.isArray(node.replies) && node.replies.length > 0 && (
          <div className="mt-2 d-flex flex-column gap-2">
            {node.replies.map((r) => (
              <CommentNode key={r.id} node={r} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <Container className="py-5">
          <div className="text-center py-5">
            <Spinner animation="border" size="sm" className="me-2" />
            <span className="text-muted">Loading discussion…</span>
          </div>
        </Container>
      </>
    );
  }

  if (!post) {
    return (
      <>
        <Navbar />
        <Container className="py-5">
          <Card className="border-0 bg-white shadow-sm">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1 fw-semibold">Discussion not found</h5>
                  <div className="text-muted small">It may have been removed or you don’t have access.</div>
                </div>
                <Button variant="primary" onClick={() => navigate("/discussions")}>
                  Back to discussions
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </>
    );
  }

  const audienceBadge = (() => {
    if (!post?.audience || post.audience === "all") return null;
    if (post.audience === "roles") {
      const roles = Array.isArray(post.allowedRoles) ? post.allowedRoles : [];
      if (roles.length === 1) {
        const only = roles[0];
        return { text: `${only} only`, variant: "info" };
      }
      if (roles.length > 1) {
        return { text: `Roles: ${roles.join(", ")}`, variant: "info" };
      }
      return { text: "Limited roles", variant: "info" };
    }
    if (post.audience === "users") {
      return { text: "Limited audience", variant: "info" };
    }
    return null;
  })();

  return (
    <>
      <Navbar />
      <PageTitle title={post.title} />
      <Container className="py-4">
        <div className="mb-3">
          <Button variant="link" className="p-0 text-decoration-none fw-semibold" onClick={() => navigate("/discussions")}>
            <i className="bi bi-arrow-left me-2" />
            Back to Discussions
          </Button>
        </div>

        <Row className="g-4">
          <Col lg={9}>
            {/* Post card - styled like the screenshot */}
            <Card className="border-0 bg-white shadow-sm rounded-4">
              <Card.Body className="p-4 p-md-5">
                <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
                  <div className="d-flex gap-3 align-items-center">
                    <UserAvatar user={post.creator} size={42} alt="" />
                    <div>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <div className="fw-semibold">
                          {post.creator?.firstName} {post.creator?.lastName}
                        </div>
                        
                      </div>
                      <div className="text-muted small">
                        {post.creator?.role || "User"} • {new Date(post.createdAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {userRole === "director" && (
                      <Button
                        variant={post.pinned ? "light-solid" : "outline-secondary"}
                        onClick={togglePostPin}
                        className="d-flex align-items-center gap-2"
                        size="sm"
                      >
                        <i className={`bi ${post.pinned ? "bi-pin-angle-fill" : "bi-pin-angle"}`} />
                        {post.pinned ? "Unpin" : "Pin"}
                      
                      </Button>
                    )}
                    {post.pinned && (
                          <span className="badge bg-light text-muted border rounded-pill">
                            <i className="bi bi-pin-angle-fill me-1" />
                            Pinned
                          </span>
                        )}
                    <Dropdown align="end">
                      <Dropdown.Toggle variant="light-solid" size="sm" className="no-arrow py-1 px-2" title="More">
                        <i className="bi bi-three-dots" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {canManagePost ? (
                          <>
                            <Dropdown.Item onClick={openEdit}>
                              <i className="bi bi-pencil me-2" />
                              Edit
                            </Dropdown.Item>
                            <Dropdown.Item className="text-danger" onClick={deletePost}>
                              <i className="bi bi-trash me-2" />
                              Delete
                            </Dropdown.Item>
                          </>
                        ) : (
                          <Dropdown.Item disabled className="text-muted">
                            No actions available
                          </Dropdown.Item>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>

                <h2 className="fw-bold mb-3" style={{ letterSpacing: "-0.02em" }}>
                  {post.title}
                </h2>

                <div className="d-flex flex-wrap gap-2 mb-3">
                  {post.category?.name && (
                    <span
                      className="badge rounded-pill"
                      style={{
                        background: (post.category.themeColor || "#64748b") + "22",
                        color: post.category.themeColor || "#64748b",
                      }}
                    >
                      <i className="bi bi-bookmark-fill me-1" />
                      {post.category.name}
                    </span>
                  )}
                  {audienceBadge && (
                    <span className={`badge rounded-pill bg-${audienceBadge.variant}-subtle text-${audienceBadge.variant}`}>
                      <i className="bi bi-people-fill me-1" />
                      {audienceBadge.text}
                    </span>
                  )}
                  {Array.isArray(post.hashtags) &&
                    post.hashtags.map((t) => (
                      <Badge
                        key={t}
                        bg="outline-light"
                        className="small cursor-pointer"
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/discussions?hashtag=${encodeURIComponent(t)}`)}
                      >
                        #{t}
                      </Badge>
                    ))}
                </div>

                <div className="eval-rich-content" style={{ lineHeight: 1.8 }} dangerouslySetInnerHTML={{ __html: post.contentHtml }} />

                <div className="mt-4 pt-3 border-top d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <div className="d-flex align-items-center gap-4 text-muted small">
                    <span className="d-flex align-items-center gap-2">
                      <i className="bi bi-hand-thumbs-up" />
                      {likeCount} Likes
                    </span>
                    <span className="d-flex align-items-center gap-2">
                      <i className="bi bi-hand-thumbs-down" />
                      {dislikeCount} Dislikes
                    </span>
                    <span className="d-flex align-items-center gap-2">
                      <i className="bi bi-chat" />
                      {comments.length} Comments
                    </span>
                    <span className="d-flex align-items-center gap-2">
                      <i className="bi bi-eye" />
                      {post.viewCount || 0} Views
                    </span>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant={myReaction === 'like' ? "primary" : "outline-secondary"}
                      size="sm"
                      className="d-flex align-items-center gap-1"
                      onClick={() => reactToPost('like')}
                    >
                      <i className="bi bi-hand-thumbs-up" />
                      Like
                    </Button>
                    <Button
                      variant={myReaction === 'dislike' ? "primary" : "outline-secondary"}
                      size="sm"
                      className="d-flex align-items-center gap-1"
                      onClick={() => reactToPost('dislike')}
                    >
                      <i className="bi bi-hand-thumbs-down" />
                      Dislike
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Comments */}
            <Card className="border-0 bg-white shadow-sm rounded-4 mt-4">
              <Card.Body className="p-4 p-md-5">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0 fw-semibold">Discussion <Badge bg="secondary" className="small">{comments.length}</Badge></h5>
                  <Button variant="light-solid" size="sm" onClick={fetchComments} disabled={commentsLoading}>
                    <i className="bi bi-arrow-clockwise me-1" />
                  </Button>
                </div>

                {/* Composer */}
                <div className="d-flex gap-3 align-items-start mb-4">
                  <div>
                      <UserAvatar user={post.creator} size={40} alt="" />
                  </div>
                  <div className="d-flex flex-row align-items-start flex-grow-1 gap-2">
                    <Form.Control
                      as="textarea"
                      rows={1}
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Share your thoughts…"
                      className="rounded-3"
                    />
                      <Button
                        variant="primary"
                        className="text-nowrap"
                        disabled={postingComment}
                        onClick={() => {
                          setReplyToId(null);
                          submitComment();
                        }}
                      >
                        <i className="bi bi-send me-2" />
                        {postingComment ? "Posting…" : "Post"}
                      </Button>
                  </div>
                </div>

                {commentsLoading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span className="text-muted">Loading comments…</span>
                  </div>
                ) : comments.length === 0 ? (
                  <div className="d-flex align-items-center flex-column gap-2">
                    <span className="text-muted d-flex align-items-center justify-content-center bg-light px-3 py-2 rounded-3"> <i className="bi bi-chat-left-text" style={{ fontSize: "2rem" }} /> </span>No comments yet. Start the conversation.</div>
                ) : (
                  <div className="d-flex flex-column gap-4">
                    {commentTree.map((node) => (
                      <CommentNode key={node.id} node={node} depth={0} />
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={3}>
            <Card className="border-0 bg-white shadow-sm rounded-4">
              <Card.Body className="p-4">
                <h6 className="mb-2 fw-semibold">About</h6>
                <div className="text-muted small">
                  Use this page to discuss topics with your program. You can like comments and add replies.
                </div>
                <hr />
                <div className="d-flex flex-column gap-2">
                  <Button variant="outline-secondary" onClick={() => navigate("/discussions")}>
                    Browse all discussions
                  </Button>
                  <Button variant="primary" onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" })}>
                    Add a comment
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Edit Post Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton className="border-bottom-0 px-4">
          <Modal.Title className="w-100">
            <div className="d-flex flex-column justify-content-start align-items-start">
              <span>Edit Discussion</span>
              <div className="text-hl"></div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="px-4">
          <Form>
            <Row className="g-2">
              <Col md={8}>
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="edit-title" label="Title" className="required-field">
                    <Form.Control
                      type="text"
                      value={editForm.title}
                      onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
                      placeholder="Title"
                    />
                  </FloatingLabel>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <FloatingLabel controlId="edit-category" label="Category">
                    <Form.Select
                      value={editForm.categoryId}
                      onChange={(e) => setEditForm((p) => ({ ...p, categoryId: e.target.value }))}
                      disabled={categoriesLoading}
                    >
                      <option value="">None</option>
                      {categories.map((c) => (
                        <option key={c.id} value={String(c.id)}>
                          {c.name}
                        </option>
                      ))}
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <FloatingLabel controlId="edit-hashtags" label="Hashtags (space/comma separated)">
                <Form.Control
                  type="text"
                  value={editForm.hashtags}
                  onChange={(e) => setEditForm((p) => ({ ...p, hashtags: e.target.value }))}
                  placeholder="#icu #airway"
                />
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label className="small text-muted required-field">Content</Form.Label>
              <RichTextEditor
                value={editForm.contentHtml}
                onChange={(html) => setEditForm((p) => ({ ...p, contentHtml: html }))}
                placeholder="Update content…"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-top-0 px-4">
          <Button variant="outline-secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveEdit} disabled={savingEdit}>
            {savingEdit ? "Saving…" : "Save changes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DiscussionPostPage;

